import { useState, useEffect, useRef } from 'react';

/**
 * Watches a loading flag and flips `timedOut` to true if loading stays active
 * past `timeoutMs`. Use in pages that fetch data on mount so users never get
 * stuck staring at a spinner while a cold backend wakes up.
 *
 * Never touches auth state, never reloads the page. Callers decide what to
 * retry.
 *
 *   const { timedOut, resetTimeout } = useLoadingTimeout(loading, fetchData);
 *   ...
 *   <LoadingTimeout timedOut={timedOut} onRetry={() => { resetTimeout(); fetchData(); }} />
 */
// eslint-disable-next-line no-unused-vars
export function useLoadingTimeout(isLoading, onRetry, timeoutMs = 8000) {
  const [timedOut, setTimedOut] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (isLoading) {
      setTimedOut(false);
      clear();
      timerRef.current = setTimeout(() => setTimedOut(true), timeoutMs);
    } else {
      clear();
      setTimedOut(false);
    }

    return clear;
  }, [isLoading, timeoutMs]);

  const resetTimeout = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setTimedOut(false);
    timerRef.current = setTimeout(() => setTimedOut(true), timeoutMs);
  };

  return { timedOut, resetTimeout };
}
